import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarCheck,
  CheckCircle,
  Clock,
  EnvelopeSimple,
  FolderOpen,
  ListChecks,
  Plus,
  Storefront,
  Users,
} from "@phosphor-icons/react";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatEventSchedule } from "@/lib/milestones";

const ACTION_META = {
  invite: { icon: EnvelopeSimple, label: "Invite clients", className: "text-secondary" },
  schedule: { icon: CalendarCheck, label: "Confirm date", className: "text-secondary" },
  run_sheet: { icon: Clock, label: "Run sheet", className: "text-accent" },
  milestone: { icon: ListChecks, label: "Planning", className: "text-primary" },
  complete: { icon: CheckCircle, label: "Complete", className: "text-accent" },
};

export default function StaffDashboard() {
  const { data, loading } = useGet("/staff/dashboard");
  if (loading) {
    return <Loading label="Loading operations" />;
  }
  const stats = data?.stats || {};
  const attention = data?.attention || [];
  const upcoming = data?.upcoming || [];

  return (
    <div>
      <PageHeader
        eyebrow="Gledswood operations"
        title="Operations desk"
        description="Prioritise client access, planning decisions and event-day approvals."
      >
        <Link to="/events">
          <Button
            className="rounded-full bg-primary hover:bg-primary/90"
            data-testid="dashboard-new-event"
          >
            <Plus size={18} className="mr-1" />
            New event
          </Button>
        </Link>
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="staff-dashboard-stats">
        <Metric icon={CalendarCheck} label="Active events" value={stats.active_events || 0} />
        <Metric icon={Users} label="Client contacts" value={stats.client_contacts || 0} />
        <Metric icon={EnvelopeSimple} label="Need an invite" value={stats.awaiting_invite || 0} />
        <Metric icon={Clock} label="Run sheet pending" value={stats.run_sheet_pending || 0} />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 xl:grid-cols-[1.35fr_0.65fr]">
        <section className="border-y border-border" data-testid="staff-attention-queue">
          <div className="flex items-end justify-between gap-4 py-4">
            <div>
              <p className="eyebrow">Needs attention</p>
              <h2 className="font-heading text-3xl text-primary mt-1">Your next actions</h2>
            </div>
            <Link
              to="/events"
              className="text-sm text-secondary hover:text-primary transition-colors duration-200"
              data-testid="dashboard-view-all-events"
            >
              View all
            </Link>
          </div>
          {attention.length === 0 ? (
            <div className="py-8 text-sm text-muted-foreground">No actions are waiting.</div>
          ) : (
            <div className="divide-y divide-border">
              {attention.map((event) => <ActionRow key={event.id} event={event} />)}
            </div>
          )}
        </section>

        <aside className="space-y-6">
          <section className="border border-border bg-card p-6 rounded-lg" data-testid="staff-upcoming-events">
            <p className="eyebrow">Upcoming celebrations</p>
            <div className="mt-4 space-y-4">
              {upcoming.length === 0 ? (
                <p className="text-sm text-muted-foreground">No future dates confirmed.</p>
              ) : upcoming.map((event) => (
                <Link
                  key={event.id}
                  to={`/events/${event.id}`}
                  className="block border-b border-border pb-4 last:border-0 last:pb-0 hover:text-secondary"
                  data-testid={`upcoming-event-${event.id}`}
                >
                  <p className="font-heading text-xl text-primary">{event.couple_names}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {formatEventSchedule(event.event_date, event.event_time)}
                  </p>
                </Link>
              ))}
            </div>
          </section>
          <div className="grid grid-cols-2 gap-3">
            <ManagementLink icon={FolderOpen} label="Resources" to="/resource-library" />
            <ManagementLink icon={Storefront} label="Suppliers" to="/preferred-suppliers" />
          </div>
        </aside>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="border border-border bg-card p-5 rounded-lg" data-testid={`staff-stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <Icon size={22} weight="duotone" className="text-secondary" />
      <p className="eyebrow mt-4">{label}</p>
      <p className="font-heading text-3xl text-primary mt-1">{value}</p>
    </div>
  );
}

function ActionRow({ event }) {
  const meta = ACTION_META[event.next_action?.kind] || ACTION_META.milestone;
  const Icon = meta.icon;
  return (
    <Link
      to={`/events/${event.id}`}
      className="group grid grid-cols-[auto_1fr_auto] gap-4 py-5 hover:bg-muted/40"
      data-testid={`attention-event-${event.id}`}
    >
      <Icon size={22} weight="duotone" className={`${meta.className} mt-1`} />
      <div className="min-w-0">
        <p className="font-heading text-2xl text-primary leading-tight">{event.couple_names}</p>
        <p className="text-sm text-muted-foreground mt-1">
          {formatEventSchedule(event.event_date, event.event_time)}
        </p>
        <p className="text-sm text-primary mt-2">{event.next_action?.label}</p>
      </div>
      <div className="flex flex-col items-end gap-2">
        <Badge variant="outline" className="rounded-full whitespace-nowrap">
          {event.progress?.complete || 0}/{event.progress?.total || 13}
        </Badge>
        <ArrowRight size={18} className="text-muted-foreground group-hover:text-primary" />
      </div>
    </Link>
  );
}

function ManagementLink({ icon: Icon, label, to }) {
  return (
    <Link
      to={to}
      className="border border-border bg-card p-4 rounded-lg hover:bg-muted transition-colors duration-200"
      data-testid={`dashboard-manage-${label.toLowerCase()}`}
    >
      <Icon size={20} weight="duotone" className="text-secondary" />
      <p className="font-heading text-lg text-primary mt-3">{label}</p>
    </Link>
  );
}
